import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface FilterControlsProps {
  priceRange: [number, number];
  setPriceRange: (value: [number, number]) => void;
  minPrice: number;
  maxPrice: number;
  hasRatings: boolean;
  setHasRatings: (value: boolean) => void;
  inStock: boolean;
  setInStock: (value: boolean) => void;
  selectedBrand: string;
  setSelectedBrand: (value: string) => void;
  availableBrands: string[];
  onPriceRangeReset?: () => void;
  selectedRating: string;
  setSelectedRating: (value: string) => void;
  selectedAvailability: string[];
  setSelectedAvailability: (value: string[]) => void;
  availableAvailabilities: string[];
  selectedDomain: string;
  setSelectedDomain: (value: string) => void;
  availableDomains: string[];
}

export const FilterControls = ({
  priceRange,
  setPriceRange,
  minPrice,
  maxPrice,
  hasRatings,
  setHasRatings,
  inStock,
  setInStock,
  selectedBrand,
  setSelectedBrand,
  availableBrands,
  onPriceRangeReset,
  selectedRating,
  setSelectedRating,
  selectedAvailability,
  setSelectedAvailability,
  availableAvailabilities,
  selectedDomain,
  setSelectedDomain,
  availableDomains,
}: FilterControlsProps) => {
  const MIN_PRICE = 1;
  const MAX_PRICE = 5000;

  const handleResetPriceRange = () => {
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    if (onPriceRangeReset) {
      onPriceRangeReset();
    }
  };

  const handleAvailabilityChange = (availability: string) => {
    if (availability === "all") {
      // If "all" is selected, clear all other selections
      setSelectedAvailability(["all"]);
    } else {
      // If any other option is selected
      const newSelection = selectedAvailability.includes(availability)
        ? selectedAvailability.filter(item => item !== availability && item !== "all")
        : [...selectedAvailability.filter(item => item !== "all"), availability];
      
      // If no options are selected, default to "all"
      setSelectedAvailability(newSelection.length === 0 ? ["all"] : newSelection);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Price Range</h3>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleResetPriceRange}
          >
            Reset
          </Button>
        </div>
        <div className="pt-2">
          <Slider
            value={[
              Math.max(MIN_PRICE, Math.min(priceRange[0], MAX_PRICE)),
              Math.max(MIN_PRICE, Math.min(priceRange[1], MAX_PRICE))
            ]}
            min={MIN_PRICE}
            max={MAX_PRICE}
            step={0.01}
            onValueChange={(value) => setPriceRange(value as [number, number])}
            className="w-full"
          />
          <div className="flex justify-between mt-2 text-sm">
            <div className="space-x-1">
              <span className="text-gray-500">Min:</span>
              <span className="font-medium">€{MIN_PRICE.toFixed(2)}</span>
              <span className="text-primary">→ €{priceRange[0].toFixed(2)}</span>
            </div>
            <div className="space-x-1">
              <span className="text-primary">€{priceRange[1].toFixed(2)} ←</span>
              <span className="font-medium">€{MAX_PRICE.toFixed(2)}</span>
              <span className="text-gray-500">:Max</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Domain</h3>
        <Select value={selectedDomain} onValueChange={setSelectedDomain}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select domain" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Domains</SelectItem>
            {availableDomains.map((domain) => (
              <SelectItem key={domain} value={domain}>
                {domain}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Brand</h3>
        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select brand" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {availableBrands.map((brand) => (
              <SelectItem key={brand} value={brand}>
                {brand}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Rating Filter</h3>
        <RadioGroup value={selectedRating} onValueChange={setSelectedRating}>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all_products" id="rating-all-products" />
              <Label htmlFor="rating-all-products">All Products</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="rating-all" />
              <Label htmlFor="rating-all">All Rated Products</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no_ratings" id="rating-none" />
              <Label htmlFor="rating-none">No Ratings</Label>
            </div>
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="flex items-center space-x-2">
                <RadioGroupItem value={rating.toString()} id={`rating-${rating}`} />
                <Label htmlFor={`rating-${rating}`}>{rating}+ Stars</Label>
              </div>
            ))}
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Availability</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="availability-all"
              checked={selectedAvailability.includes("all")}
              onCheckedChange={() => handleAvailabilityChange("all")}
            />
            <Label htmlFor="availability-all">All Availability</Label>
          </div>
          {availableAvailabilities.map((availability) => (
            <div key={availability} className="flex items-center space-x-2">
              <Checkbox 
                id={`availability-${availability}`}
                checked={selectedAvailability.includes(availability)}
                onCheckedChange={() => handleAvailabilityChange(availability)}
              />
              <Label htmlFor={`availability-${availability}`}>{availability}</Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
